import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../utils/constants';
import toast from 'react-hot-toast';
import './RegisterPage.css';

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// Strip to raw digits only
function getDigits(raw) {
  return raw.replace(/\D/g, '');
}

// Format digits into (XXX)XXX-XXXX display format as user types
function formatPhoneDisplay(raw) {
  const digits = getDigits(raw);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

// Format phone for Firebase: strip non-digits, auto-prepend +1 for US numbers
function formatPhone(raw) {
  const digits = getDigits(raw);
  return `+1${digits}`;
}

// Check if phone number is valid (exactly 10 digits for US numbers)
function isValidPhone(raw) {
  const digits = getDigits(raw);
  // If they entered a country code (e.g. 18475551234), strip leading 1
  if (digits.length === 11 && digits.startsWith('1')) {
    return true;
  }
  return digits.length === 10;
}

// Check if email is valid
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function RegisterPage() {
  const [step, setStep] = useState(1); // 1 = form, 2 = phone verify
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [resendCooldown, setResendCooldown] = useState(0);

  const usernameTimer = useRef(null);
  const cooldownTimer = useRef(null);

  const {
    register,
    sendPhoneVerification,
    verifyPhoneAndCreateProfile,
    checkUsernameAvailable,
    loading,
    error,
    clearError,
  } = useAuth();

  const { isAuthenticated, userProfile } = useAuthContext();
  const navigate = useNavigate();

  // Redirect if fully registered
  useEffect(() => {
    if (isAuthenticated && userProfile?.username) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [isAuthenticated, userProfile, navigate]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (usernameTimer.current) clearTimeout(usernameTimer.current);
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  // Debounced username availability check
  const handleUsernameChange = (value) => {
    setFormData((prev) => ({ ...prev, username: value }));
    setFormErrors((prev) => ({ ...prev, username: null }));
    clearError();

    if (usernameTimer.current) clearTimeout(usernameTimer.current);

    if (!value || value.length < 3) {
      setUsernameStatus(null);
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      setUsernameStatus(null);
      return;
    }

    setUsernameStatus('checking');
    usernameTimer.current = setTimeout(async () => {
      try {
        const available = await checkUsernameAvailable(value);
        setUsernameStatus(available ? 'available' : 'taken');
      } catch {
        setUsernameStatus(null);
      }
    }, 500);
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      // Only allow digits — strip anything else, cap at 10 digits
      const digits = getDigits(value).slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: formatPhoneDisplay(digits) }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    setFormErrors((prev) => ({ ...prev, [field]: null }));
    clearError();
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 20) {
      errors.username = 'Username must be 20 characters or less';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      errors.username = 'Only letters, numbers, and underscores allowed';
    } else if (usernameStatus === 'taken') {
      errors.username = 'Username is already taken';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 1: Submit registration form
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Check username one more time
    if (usernameStatus !== 'available') {
      const available = await checkUsernameAvailable(formData.username);
      if (!available) {
        setFormErrors((prev) => ({ ...prev, username: 'Username is already taken' }));
        return;
      }
    }

    // Create Firebase Auth account
    const user = await register(formData.email, formData.password);
    if (!user) return; // error is set by the hook

    // On localhost: skip phone verification entirely, create profile directly
    if (isLocalhost) {
      console.log('[DEV] Localhost detected — skipping SMS, creating profile directly');
      const phoneFormatted = formatPhone(formData.phone);
      const success = await verifyPhoneAndCreateProfile('000000', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim(),
        username: formData.username.trim(),
        phone: phoneFormatted,
      });

      if (success) {
        toast.success('Welcome to Streak for Cash!');
        navigate(ROUTES.HOME, { replace: true });
      }
      return;
    }

    // Production: Send phone verification SMS
    const phoneFormatted = formatPhone(formData.phone);
    const sent = await sendPhoneVerification(phoneFormatted, 'register-submit-btn');
    if (sent) {
      setStep(2);
      startResendCooldown();
    }
  };

  // Step 2: Verify phone code
  const handleVerifySubmit = async (e) => {
    e.preventDefault();

    const phoneFormatted = formatPhone(formData.phone);
    const success = await verifyPhoneAndCreateProfile(verificationCode, {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      displayName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      email: formData.email.trim(),
      username: formData.username.trim(),
      phone: phoneFormatted,
    });

    if (success) {
      toast.success('Welcome to Streak for Cash!');
      navigate(ROUTES.HOME, { replace: true });
    }
  };

  // Resend SMS with cooldown
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    clearError();

    const phoneFormatted = formatPhone(formData.phone);

    // Clear old reCAPTCHA
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (_) {}
      window.recaptchaVerifier = null;
    }

    const sent = await sendPhoneVerification(phoneFormatted, 'resend-code-btn');
    if (sent) {
      toast.success('New code sent!');
      startResendCooldown();
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Render Step 1: Registration Form
  if (step === 1) {
    return (
      <div className="register-page">
        <div className="register-card">
          <div className="register-header">
            <span className="register-icon">🔥</span>
            <h1>Create Account</h1>
            <p>Join Streak for Cash and start picking winners</p>
          </div>

          <form onSubmit={handleRegisterSubmit} className="register-form">
            {/* First Name */}
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                placeholder="John"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                autoFocus
              />
              {formErrors.firstName && <span className="field-error">{formErrors.firstName}</span>}
            </div>

            {/* Last Name */}
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
              {formErrors.lastName && <span className="field-error">{formErrors.lastName}</span>}
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-with-status">
                <input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
                {formData.email.length > 0 && isValidEmail(formData.email) && (
                  <span className="input-status available">✓</span>
                )}
                {formData.email.length > 0 && !isValidEmail(formData.email) && (
                  <span className="input-status taken">✗</span>
                )}
              </div>
              {formErrors.email && <span className="field-error">{formErrors.email}</span>}
            </div>

            {/* Username */}
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-with-status">
                <input
                  id="username"
                  type="text"
                  placeholder="SportsFan42"
                  value={formData.username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  maxLength={20}
                  required
                />
                {usernameStatus === 'checking' && <span className="input-status checking">...</span>}
                {usernameStatus === 'available' && <span className="input-status available">✓</span>}
                {usernameStatus === 'taken' && <span className="input-status taken">✗</span>}
              </div>
              <span className="form-hint">3-20 characters. Letters, numbers, and underscores only.</span>
              {formErrors.username && <span className="field-error">{formErrors.username}</span>}
              {usernameStatus === 'taken' && !formErrors.username && (
                <span className="field-error">Username is already taken</span>
              )}
            </div>

            {/* Phone */}
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <div className="input-with-status">
                <input
                  id="phone"
                  type="tel"
                  placeholder="(555)555-5555"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
                {formData.phone.length > 0 && isValidPhone(formData.phone) && (
                  <span className="input-status available">✓</span>
                )}
                {formData.phone.length > 0 && !isValidPhone(formData.phone) && (
                  <span className="input-status taken">✗</span>
                )}
              </div>
              <span className="form-hint">We'll send a verification code to this number.</span>
              {formErrors.phone && <span className="field-error">{formErrors.phone}</span>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-with-status">
                <input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                />
                {formData.password.length > 0 && formData.password.length >= 6 && (
                  <span className="input-status available">✓</span>
                )}
                {formData.password.length > 0 && formData.password.length < 6 && (
                  <span className="input-status taken">✗</span>
                )}
              </div>
              <span className="form-hint">Minimum 6 characters.</span>
              {formErrors.password && <span className="field-error">{formErrors.password}</span>}
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-with-status">
                <input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  required
                />
                {formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword && (
                  <span className="input-status available">✓</span>
                )}
                {formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword && (
                  <span className="input-status taken">✗</span>
                )}
              </div>
              {formErrors.confirmPassword && <span className="field-error">{formErrors.confirmPassword}</span>}
            </div>

            {error && <div className="register-error">{error}</div>}

            <button
              id="register-submit-btn"
              type="submit"
              className="btn btn-primary btn-full"
              disabled={
                loading ||
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                !isValidEmail(formData.email) ||
                usernameStatus !== 'available' ||
                !isValidPhone(formData.phone) ||
                formData.password.length < 6 ||
                formData.password !== formData.confirmPassword
              }
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="auth-switch">
            Already have an account? <Link to={ROUTES.LOGIN}>Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  // Render Step 2: Phone Verification
  return (
    <div className="register-page">
      <div className="register-card">
        <div className="register-header">
          <span className="register-icon">📱</span>
          <h1>Verify Your Phone</h1>
          <p>
            We sent a 6-digit code to{' '}
            <strong>{formatPhone(formData.phone)}</strong>
          </p>
        </div>

        <form onSubmit={handleVerifySubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              id="code"
              type="text"
              placeholder="123456"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                clearError();
              }}
              maxLength={6}
              required
              autoFocus
              className="verification-input"
            />
            <span className="form-hint">Enter the 6-digit code sent to your phone</span>
          </div>

          {error && <div className="register-error">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading || verificationCode.length < 6}
          >
            {loading ? 'Verifying...' : 'Verify & Complete Registration'}
          </button>
        </form>

        <div className="resend-section">
          <button
            id="resend-code-btn"
            className="btn-link"
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || loading}
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : "Didn't get the code? Resend"}
          </button>
        </div>

        <div className="auth-switch">
          <button className="btn-link" onClick={() => setStep(1)}>
            ← Back to Register
          </button>
        </div>
      </div>
    </div>
  );
}
